/**
 * AI Naming Executor
 *
 * Handles the 'ai-naming' and 'pipeline-ai-naming' node execution.
 * Calls the ModernizeIT API Layer /ainaming endpoint.
 *
 * API Flow:
 * 1. POST /ainaming → returns job_id and results (may complete immediately)
 * 2. Poll GET /jobs/{job_id} → wait for SUCCEEDED status (worker mode)
 * 3. Retrieve naming suggestions from job output_data
 *
 * This node must run AFTER Code Analysis to get paragraph metadata.
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the AI naming node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and artifacts
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing AI naming analysis...')

  // Check for abort before starting
  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // CRITICAL: Need code_analysis_job_id from context (set by Code Analysis node)
  const codeAnalysisJobId = context.code_analysis_job_id || context.jobs?.code_analysis_job_id
  if (!codeAnalysisJobId) {
    throw new Error(
      'AI Naming requires Code Analysis to run first. ' +
      'Connect the Code Analysis node output to this node input.'
    )
  }

  // 1. Build the request body as FormData (API expects Form fields, not JSON)
  const formData = new FormData()
  formData.append('code_analysis_job_id', codeAnalysisJobId)
  formData.append('scout_account_id', context.scout_account_id)
  formData.append('application_name', context.application_name)
  formData.append('user_id', context.user_id)
  formData.append('enable_ai', config.enable_ai !== false ? 'true' : 'false')
  formData.append('confidence_threshold', String(config.confidence_threshold ?? 0.75))
  formData.append('auto_approve_high_confidence', config.auto_approve_high_confidence ? 'true' : 'false')

  onProgress(5, `Analyzing paragraphs from ${codeAnalysisJobId}...`)

  // 2. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}/ainaming`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    // Check for API-level error in response
    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'AI Naming failed')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  onProgress(10, `Job started: ${jobId}`)

  // 3. Check if job completed immediately (synchronous execution)
  if (jobResponse.status === 'completed' || jobResponse.status === 'SUCCEEDED') {
    onProgress(50, 'AI naming complete')
  } else {
    // 4. Worker mode: Poll for completion using shared authenticated poller
    onProgress(15, 'Generating semantic names...')
    const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
      maxWaitSeconds: 1800, // AI Naming takes 10-15 min depending on paragraph count
      pollInterval: 2000,
      onProgress: (progress, message) => {
        // Map polling progress from 15-95%
        const mappedProgress = 15 + (progress * 0.80)
        onProgress(mappedProgress, message)
      },
      signal
    })

    // Merge completed job data
    Object.assign(jobResponse, completedJob)
  }

  onProgress(100, 'AI naming complete')

  // 5. Return formatted result
  return formatResult(jobId, jobResponse)
}

/**
 * Format the result object for consistent structure
 */
function formatResult(jobId, data) {
  return {
    // Job tracking
    jobs: {
      ai_naming_job_id: jobId,
    },

    // Status
    success: data.success !== false,
    status: data.status || 'completed',

    // Paths
    artifacts_path: data.artifacts_path,

    // Execution metrics
    duration_ms: data.duration_ms,

    // Summary statistics
    summary: data.summary || {},

    // Artifacts produced
    artifacts: data.artifacts || {},

    // Error info if any
    error: data.error || null,
  }
}

export default {
  execute,
}
